import { alpha, useTheme } from '@mui/material';
import { LoadingIndicator } from '@qfa/react-components';
import { useDimension } from 'kineticUtils';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import shortid from 'shortid';

import SelectionContext from './columns/SelectionContext';
import ColumnsContext, { ColumnConfig } from './contexts/ColumnsContext';
import { useTableSelection } from './hooks';
import TableView from './TableView';
import { BaseRow, GenericTable, TableProps, TableRefProps } from './types';
import { exportAsCSV, orderColumns } from './utils';
import { TablePropsContext } from './VirtualTable';

// nice to have
// skeleton loading
// support formRow
// horizontal scroll on small screen

const defaultSelection: string[] = [];

const Table = forwardRef<TableRefProps, TableProps<BaseRow>>((props, ref) => {
  const {
    listRef,
    children,
    data,
    selection = defaultSelection,
    setSelection,
    loading,
    onSortChange,
    extraContent,
    renderView,
    hideColumnHeader = false,
    rowOptions: {
      type,
      onClick: onRowClick,
      onContextClick: onRowContextClick,
      hoverColor,
      selectedColor,
      setClassName,
      subRow,
    } = {},
    sort,
    ...tableProps
  } = props;
  const instanceIdRef = useRef(shortid());

  const [columns, setColumns] = useState<ColumnConfig[]>([]);

  const addColumn = useCallback((newColumn: ColumnConfig, isMount?: boolean) => {
    setColumns((prev) => {
      const newColumns = [...prev];
      const foundIndex = prev.findIndex((column) => column.name === newColumn.name);
      if (foundIndex > -1) {
        let updatedColumn = {
          ...newColumn,
        };
        // if it is the first mount of the column, the controls may have already added state,
        // and we dont want to override that
        if (isMount) {
          updatedColumn = {
            ...updatedColumn,
            ...prev[foundIndex],
          };
        }

        newColumns.splice(foundIndex, 1, updatedColumn);
      } else {
        newColumns.push(newColumn);
      }
      return newColumns;
    });
  }, []);

  const getColumn = useCallback(
    (needleName: string) => {
      return columns.find(({ name }) => needleName === name);
    },
    [columns]
  );

  const { selectRow, selectAll, deselectRow, deselectAll, isAllSelected } = useTableSelection({
    selection,
    setSelection,
    data,
  });

  const orderedColumns = useMemo(
    () => orderColumns(columns).filter((column) => column.visible),
    [columns]
  );

  const theme = useTheme();

  const getHoverColor = () => {
    if (hoverColor === false) {
      return 'transparent !important';
    }
    const color =
      typeof hoverColor === 'string' ? hoverColor : alpha(theme.palette.primary.light, 0.7);
    // MUI row hover is strangely scoped
    return `${color} !important`;
  };
  const getSelectedColor = () => {
    if (selectedColor === false) {
      return 'transparent !important';
    }
    if (typeof selectedColor === 'string') {
      return `${selectedColor} !important`;
    }

    return ``;
  };

  useImperativeHandle(
    ref,
    () => ({
      download(fileName: string, additionalData?: string[]) {
        exportAsCSV(fileName, columns, data || [], additionalData);
      },
      getColumns() {
        return columns;
      },
    }),
    [columns, data]
  );

  const renderRowStyles = () => {
    return (
      <style>
        #tableWrapper_{instanceIdRef.current} tbody tr:hover
        {`{background-color: ${getHoverColor()};}`}
        #tableWrapper_{instanceIdRef.current} tbody tr.row_selected
        {`{background-color: ${getSelectedColor()};}`}
      </style>
    );
  };

  const { Provider } = ColumnsContext;
  const { Provider: SelectionProvider } = SelectionContext;
  const wrapperRef = useRef(null);
  const { height } = useDimension(wrapperRef);

  const isSelected = useCallback((id: string) => selection.includes(id), [selection]);

  return (
    <>
      {renderRowStyles()}
      <Provider
        value={{
          columns,
          getColumn,
          setColumns,
          addColumn,
        }}
      >
        <SelectionProvider
          value={{ selection, isAllSelected, selectRow, deselectRow, selectAll, deselectAll }}
        >
          {children}
        </SelectionProvider>
      </Provider>
      <div
        ref={wrapperRef}
        style={{
          flex: '1 0 auto',
          height: 'calc(100% - 1px)',
          width: '100%',
          minHeight: 'inherit',
          overflowX: 'auto',
        }}
        id={`tableWrapper_${instanceIdRef.current}`}
      >
        <div style={{ position: 'relative', height: '100%', minHeight: 'inherit' }}>
          {loading && <LoadingIndicator />}

          <TablePropsContext.Provider
            value={{
              extraContent,
              table: tableProps,
              tHead: {
                hasSubRow: !!subRow,
                onSortChange: onSortChange,
                orderedColumns: orderedColumns,
                sort,
              },
              hideColumnHeader,
            }}
          >
            <DndProvider backend={HTML5Backend}>
              <TableView
                listRef={listRef}
                tableHeight={height}
                data={data}
                orderedColumns={orderedColumns}
                type={type}
                onClick={onRowClick}
                onContextClick={onRowContextClick}
                subRow={subRow}
                isSelected={isSelected}
                setClassName={setClassName}
                render={renderView}
                extraContent={extraContent}
              />
            </DndProvider>
          </TablePropsContext.Provider>
        </div>
      </div>
    </>
  );
});

export default memo(Table) as GenericTable;
